import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, IconButton, Typography } from '@one-thd/sui-atomic-components';
import {
  Close, Document, ErrorFilled, PDF, Upload
} from '@one-thd/sui-icons';
import {
  ACCEPTED_FILE_TYPES,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  MAX_FILES,
  REJECTION_REASONS,
  UPLOAD_STATES,
  getUnsupportedFileError,
  readFileAsBase64,
  resolveMimeType
} from './aiListBuilderHelpers';

/**
 * Optional reference file upload for the AI List Builder.
 *
 * Images, PDFs, spreadsheets (.xlsx), CSVs and plain text — see UPLOAD_TYPES.
 * Click-to-browse only. Files are capped individually at MAX_FILE_SIZE_BYTES
 * and in count at MAX_FILES (currently 1, since the list agent only ingests
 * one file per request); the control stays enabled past either cap, but a
 * selection that would exceed one is rejected with an error instead of
 * being staged. Only the first staged file is read and submitted via
 * onImageChange — any further ones are visual-only until the agent supports
 * more than one. No file is stored client-side beyond the interaction.
 */
export const ReferenceImageUpload = ({ onImageChange, onUploadingChange, disabled }) => {
  const fileInputRef = useRef(null);
  // Bumped on every new primary read or removal, so a stale in-flight read can detect it's superseded.
  const selectionIdRef = useRef(0);
  const nextFileIdRef = useRef(0);
  const [selectedFiles, setSelectedFiles] = useState([]); // [{ id, file }], selectedFiles[0] is the submitted one
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileType, setFileType] = useState('');
  const [uploadState, setUploadState] = useState(UPLOAD_STATES.IDLE);
  const [uploadError, setUploadError] = useState('');
  const [uploadErrorReason, setUploadErrorReason] = useState(null);

  // Revoke the object URL when it changes or the component unmounts.
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Lets the parent block Generate until the read resolves and onImageChange fires.
  useEffect(() => {
    onUploadingChange(uploadState === UPLOAD_STATES.UPLOADING);
  }, [uploadState, onUploadingChange]);

  const clearPrimaryPreview = useCallback(() => {
    setPreviewUrl(null);
    setFileName('');
    setFileType('');
    setUploadState(UPLOAD_STATES.IDLE);
  }, []);

  const attachPrimary = useCallback((file) => {
    selectionIdRef.current += 1;
    const selectionId = selectionIdRef.current;

    // handleFileSelect already validated size/type before staging, so this only
    // needs the canonical MIME (not file.type — see resolveMimeType) for display/submission.
    const mimeType = resolveMimeType(file.name);
    setFileName(file.name);
    setFileType(mimeType);
    setPreviewUrl(URL.createObjectURL(file));
    setUploadState(UPLOAD_STATES.UPLOADING);

    readFileAsBase64(file)
      .then(({ base64 }) => {
        if (selectionIdRef.current !== selectionId) return; // superseded, ignore
        setUploadState(UPLOAD_STATES.COMPLETE);
        onImageChange({ base64, mimeType, fileName: file.name });
      })
      .catch((err) => {
        if (selectionIdRef.current !== selectionId) return; // superseded, ignore
        setUploadState(UPLOAD_STATES.ERROR);
        setUploadError(err.message || 'Failed to read file. Please try again.');
        onImageChange(null);
      });
  }, [onImageChange]);

  // Only clear the submission/flip to error when nothing is staged yet — otherwise
  // an already-loaded primary's status should stay visible alongside the error.
  const rejectSelection = useCallback((message, reason) => {
    setUploadError(message);
    setUploadErrorReason(reason);
    if (selectedFiles.length === 0) {
      setUploadState(UPLOAD_STATES.ERROR);
      onImageChange(null);
    }
  }, [selectedFiles.length, onImageChange]);

  const handleFileSelect = useCallback((event) => {
    const incoming = Array.from(event.target.files || []);
    // Reset so selecting the same file name(s) again still fires a change event.
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (incoming.length === 0) return;

    const oversized = incoming.find((file) => file.size > MAX_FILE_SIZE_BYTES);
    if (oversized) {
      rejectSelection(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`, REJECTION_REASONS.FILE_TOO_LARGE);
      return;
    }

    // Validate on the extension, not file.type: browsers report a .csv as
    // text/csv, application/vnd.ms-excel or '' depending on the machine, so a
    // MIME check rejects valid files and can't tell a .csv from a .xls.
    const invalidFile = incoming.find((file) => !resolveMimeType(file.name));
    if (invalidFile) {
      rejectSelection(getUnsupportedFileError(invalidFile.name), REJECTION_REASONS.INVALID_FILE_TYPE);
      return;
    }

    const newEntries = incoming.map((file) => {
      const id = nextFileIdRef.current;
      nextFileIdRef.current = id + 1;
      return { id, file };
    });
    const combined = [...selectedFiles, ...newEntries];

    if (combined.length > MAX_FILES) {
      rejectSelection(
        `You can attach up to ${MAX_FILES} file${MAX_FILES === 1 ? '' : 's'}.`,
        REJECTION_REASONS.FILE_COUNT_EXCEEDED
      );
      return;
    }

    setUploadError('');
    setUploadErrorReason(null);
    const wasEmpty = selectedFiles.length === 0;
    setSelectedFiles(combined);
    if (wasEmpty) {
      attachPrimary(combined[0].file);
    }
  }, [selectedFiles, attachPrimary, rejectSelection]);

  const handleRemoveFile = useCallback((id) => {
    const removedWasPrimary = selectedFiles[0]?.id === id;
    const next = selectedFiles.filter((entry) => entry.id !== id);
    setSelectedFiles(next);
    setUploadError('');
    setUploadErrorReason(null);

    if (!removedWasPrimary) return;

    selectionIdRef.current += 1; // invalidate any read still in flight for the removed primary
    clearPrimaryPreview();
    onImageChange(null);
    if (next.length > 0) {
      attachPrimary(next[0].file);
    }
  }, [selectedFiles, clearPrimaryPreview, onImageChange, attachPrimary]);

  const handleChoose = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  return (
    <div className="sui-flex sui-flex-col sui-gap-2">
      <input
        ref={fileInputRef}
        type="file"
        multiple={MAX_FILES > 1}
        accept={ACCEPTED_FILE_TYPES}
        onChange={handleFileSelect}
        disabled={disabled}
        className="sui-hidden"
        data-testid="reference-image-input"
      />

      {previewUrl && (
        <div
          className="sui-flex sui-items-center sui-gap-3 sui-px-2 sui-h-11 sui-border-1
            sui-border-solid sui-border-primary sui-rounded-lg"
        >
          <div className="sui-relative sui-w-8 sui-h-8 sui-rounded sui-overflow-hidden sui-flex-shrink-0">
            {fileType.startsWith('image/') ? (
              <img
                width={32}
                height={32}
                src={previewUrl}
                alt={`Reference preview of ${fileName}`}
                className="sui-w-full sui-h-full sui-object-cover"
                data-testid="reference-image-preview"
              />
            ) : (
              <div
                className="sui-w-full sui-h-full sui-flex sui-items-center sui-justify-center"
                data-testid={fileType === 'application/pdf'
                  ? 'reference-image-pdf-preview'
                  : 'reference-file-icon-preview'}
              >
                {fileType === 'application/pdf'
                  ? <PDF size="regular" color="medium" />
                  : <Document size="regular" color="medium" />}
              </div>
            )}
          </div>
          <div className="sui-flex sui-flex-col sui-flex-1 sui-min-w-0">
            <Typography variant="body-sm" weight="bold" truncate>
              {fileName}
            </Typography>
            {uploadState === UPLOAD_STATES.UPLOADING && (
              <Typography variant="body-sm" color="subtle">Loading...</Typography>
            )}
            {uploadState === UPLOAD_STATES.COMPLETE && (
              <Typography variant="body-sm" color="success">Loaded &amp; ready</Typography>
            )}
          </div>
          <IconButton
            icon={Close}
            aria-label="Remove photo"
            onClick={() => handleRemoveFile(selectedFiles[0].id)}
            disabled={disabled}
            data-testid="reference-image-remove"
          />
        </div>
      )}

      {selectedFiles.slice(1).map(({ id, file }) => (
        <div
          key={id}
          className="sui-flex sui-items-center sui-gap-3 sui-px-2 sui-h-11 sui-border-1
            sui-border-solid sui-border-primary sui-rounded-lg"
          data-testid="reference-image-additional-file"
        >
          <div className="sui-flex sui-flex-col sui-flex-1 sui-min-w-0">
            <Typography variant="body-sm" weight="bold" truncate>
              {file.name}
            </Typography>
          </div>
          <IconButton
            icon={Close}
            aria-label={`Remove ${file.name}`}
            onClick={() => handleRemoveFile(id)}
            disabled={disabled}
            data-testid="reference-image-remove-additional"
          />
        </div>
      ))}

      <Button
        variant="tertiary"
        startIcon={Upload}
        onClick={handleChoose}
        disabled={disabled}
        fullWidth
        data-testid="reference-image-choose"
      >
        Upload File
      </Button>

      {uploadError && (
        <span className="sui-flex sui-items-top sui-gap-1 sui-text-danger">
          <ErrorFilled color="danger" />
          <Typography
            color="danger"
            variant="body-sm"
            data-testid="reference-image-error"
            data-reason={uploadErrorReason}
          >
            {uploadError}
          </Typography>
        </span>
      )}
    </div>
  );
};

ReferenceImageUpload.displayName = 'ReferenceImageUpload';

ReferenceImageUpload.propTypes = {
  onImageChange: PropTypes.func.isRequired,
  onUploadingChange: PropTypes.func,
  disabled: PropTypes.bool,
};

ReferenceImageUpload.defaultProps = {
  onUploadingChange: () => {},
  disabled: false,
};
